import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, RefreshCw, AlertTriangle, Check, Globe } from 'lucide-react';
import { api } from '../utils/api';
import { getAuthHeaders } from '../lib/authHeaders';

interface APIKey {
  id: string;
  org_id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  rate_limit: number;
  created_at: string;
  expires_at?: string;
  last_used?: string;
  is_active: boolean;
}

interface Webhook {
  id: string;
  org_id: string;
  url: string;
  events: string[];
  is_active: boolean;
  created_at: string;
  last_triggered?: string;
  failure_count: number;
}

interface APISettingsProps {
  orgId: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_PERMISSIONS = [
  { id: 'read:reports', label: 'Read Reports', description: 'View claim data and reports' },
  { id: 'read:pdf', label: 'Read PDFs', description: 'Download PDF reports' },
  { id: 'write:reports', label: 'Write Reports', description: 'Create and update reports' },
  { id: 'manage:shares', label: 'Manage Shares', description: 'Create/revoke share links' },
  { id: 'admin', label: 'Admin', description: 'Full access (trusted partners)' }
];

const WEBHOOK_EVENTS = [
  { id: 'claim.created', label: 'Claim Created' },
  { id: 'claim.updated', label: 'Claim Updated' },
  { id: 'claim.calculated', label: 'Claim Calculated' },
  { id: 'settlement.updated', label: 'Settlement Updated' }
];

export default function APISettings({ orgId }: APISettingsProps) {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Create key form
  const [keyName, setKeyName] = useState('');
  const [keyPermissions, setKeyPermissions] = useState<string[]>(['read:reports']);
  const [keyRateLimit, setKeyRateLimit] = useState(1000);
  const [keyExpiresDays, setKeyExpiresDays] = useState(365);

  // Create webhook form
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const fetchData = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const [keysData, webhooksData] = await Promise.all([
        api.get(`/api/v1/external/keys?org_id=${orgId}`, { showErrorToast: false }),
        api.get('/api/v1/external/webhooks', {
          headers: authHeaders,
          showErrorToast: false
        })
      ]);

      setApiKeys(keysData || []);
      setWebhooks(webhooksData || []);
    } catch (error) {
      console.error('Error fetching API data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const data = await api.post('/api/v1/external/keys', {
        org_id: orgId,
        name: keyName,
        permissions: keyPermissions,
        rate_limit: keyRateLimit,
        expires_in_days: keyExpiresDays
      });

      setNewKeySecret(data.api_key);
      setApiKeys([...apiKeys, data]);
      setShowCreateKey(false);
      setKeyName('');
      setKeyPermissions(['read:reports']);
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      const data = await api.post('/api/v1/external/webhooks',
        { url: webhookUrl, events: webhookEvents },
        { headers: await getAuthHeaders() }
      );

      setWebhooks([...webhooks, data]);
      setShowCreateWebhook(false);
      setWebhookUrl('');
      setWebhookEvents([]);
    } catch (error) {
      console.error('Error creating webhook:', error);
    }
  };

  const handleCopyKey = () => {
    if (newKeySecret) {
      navigator.clipboard.writeText(newKeySecret);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key?')) return;

    try {
      await api.delete(`/api/v1/external/keys/${keyId}`, undefined, {
        headers: await getAuthHeaders()
      });
      setApiKeys(apiKeys.filter(k => k.id !== keyId));
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await api.delete(`/api/v1/external/webhooks/${webhookId}`, undefined, {
        headers: await getAuthHeaders()
      });
      setWebhooks(webhooks.filter(w => w.id !== webhookId));
    } catch (error) {
      console.error('Error deleting webhook:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="rounded-xl shadow-lg p-8 animate-pulse" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="h-6 rounded w-1/3 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
        <div className="space-y-3">
          <div className="h-16 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
          <div className="h-16 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* New Key Secret Modal */}
      {newKeySecret && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-2xl max-w-lg w-full p-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
                <Key className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>API Key Created!</h3>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Save this key now - it won't be shown again</p>
              </div>
            </div>

            <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-bg-base)' }}>
              <div className="flex items-center justify-between">
                <code className="text-sm break-all" style={{ color: 'var(--color-verified)' }}>{newKeySecret}</code>
                <button
                  onClick={handleCopyKey}
                  className="ml-2 p-2 rounded transition"
                  style={{ color: 'var(--color-text-dim)' }}
                >
                  {copiedKey ? (
                    <Check className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                  ) : (
                    <Copy className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>
              </div>
            </div>

            <div className="border rounded-lg p-3 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-center gap-2" style={{ color: 'var(--color-brand)' }}>
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Store this key securely</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                This is the only time you'll see this key. If you lose it, you'll need to create a new one.
              </p>
            </div>

            <button
              onClick={() => setNewKeySecret(null)}
              className="w-full py-2 rounded-lg font-semibold"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              I've saved my key
            </button>
          </div>
        </div>
      )}

      {/* API Keys Section */}
      <div className="rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-bg-base)', color: 'var(--color-text-primary)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">API Keys</h2>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Manage programmatic access to your data</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateKey(true)}
              className="px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Plus className="w-4 h-4" />
              Create Key
            </button>
          </div>
        </div>

        <div className="p-6">
          {apiKeys.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
              <Key className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No API keys created yet</p>
              <p className="text-sm mt-1">Create a key to start using the API</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map(key => (
                <div
                  key={key.id}
                  className="p-4 border rounded-lg"
                  style={key.is_active ? { borderColor: 'var(--color-border)' } : { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{key.name}</span>
                        {!key.is_active && (
                          <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Revoked</span>
                        )}
                      </div>
                      <code className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{key.key_prefix}••••••••</code>
                    </div>
                    {key.is_active && (
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="p-2 transition"
                        style={{ color: 'var(--color-text-dim)' }}
                        data-testid={`revoke-key-${key.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {key.permissions.map(p => (
                      <span key={p} className="px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>{p}</span>
                    ))}
                    <span className="px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      {key.rate_limit}/hour
                    </span>
                  </div>
                  <div className="mt-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>
                    Created {formatDate(key.created_at)}
                    {key.last_used && ` • Last used ${formatDate(key.last_used)}`}
                    {key.expires_at && ` • Expires ${formatDate(key.expires_at)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Key Form */}
        {showCreateKey && (
          <div className="border-t p-6" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
            <h3 className="font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Create New API Key</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Key Name *</label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., Production Server"
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Permissions</label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_PERMISSIONS.map(perm => (
                    <label
                      key={perm.id}
                      className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer"
                      style={keyPermissions.includes(perm.id) ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' } : { borderColor: 'var(--color-border)' }}
                    >
                      <input
                        type="checkbox"
                        checked={keyPermissions.includes(perm.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setKeyPermissions([...keyPermissions, perm.id]);
                          } else {
                            setKeyPermissions(keyPermissions.filter(p => p !== perm.id));
                          }
                        }}
                        className="rounded"
                        style={{ accentColor: 'var(--color-brand)' }}
                      />
                      <div>
                        <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{perm.label}</span>
                        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{perm.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Rate Limit (req/hour)</label>
                  <input
                    type="number"
                    value={keyRateLimit}
                    onChange={(e) => setKeyRateLimit(parseInt(e.target.value) || 1000)}
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Expires In (days)</label>
                  <input
                    type="number"
                    value={keyExpiresDays}
                    onChange={(e) => setKeyExpiresDays(parseInt(e.target.value) || 365)}
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateKey(false)}
                  className="flex-1 py-2 border rounded-lg font-medium"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKey}
                  disabled={!keyName}
                  className="flex-1 py-2 rounded-lg font-medium disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  Create Key
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Webhooks Section */}
      <div className="rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">Webhooks</h2>
                <p className="text-sm" style={{ color: 'var(--color-on-brand)' }}>Receive real-time notifications</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateWebhook(true)}
              className="px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)' }}
            >
              <Plus className="w-4 h-4" />
              Add Webhook
            </button>
          </div>
        </div>

        <div className="p-6">
          {webhooks.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
              <Globe className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No webhooks configured</p>
              <p className="text-sm mt-1">Add a webhook to receive event notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map(webhook => (
                <div
                  key={webhook.id}
                  className="p-4 border rounded-lg"
                  style={webhook.is_active ? { borderColor: 'var(--color-border)' } : { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <code className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{webhook.url}</code>
                      {webhook.failure_count > 0 && (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}>
                          {webhook.failure_count} failures
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="p-2 transition"
                      style={{ color: 'var(--color-text-dim)' }}
                      data-testid={`delete-webhook-${webhook.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {webhook.events.map(event => (
                      <span key={event} className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                        {event}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>
                    Created {formatDate(webhook.created_at)}
                    {webhook.last_triggered && ` • Last triggered ${formatDate(webhook.last_triggered)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Webhook Form */}
        {showCreateWebhook && (
          <div className="border-t p-6" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
            <h3 className="font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Add Webhook</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Endpoint URL *</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Events</label>
                <div className="grid grid-cols-2 gap-2">
                  {WEBHOOK_EVENTS.map(event => (
                    <label
                      key={event.id}
                      className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer"
                      style={webhookEvents.includes(event.id) ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' } : { borderColor: 'var(--color-border)' }}
                    >
                      <input
                        type="checkbox"
                        checked={webhookEvents.includes(event.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWebhookEvents([...webhookEvents, event.id]);
                          } else {
                            setWebhookEvents(webhookEvents.filter(ev => ev !== event.id));
                          }
                        }}
                        className="rounded"
                        style={{ accentColor: 'var(--color-brand)' }}
                      />
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{event.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateWebhook(false)}
                  className="flex-1 py-2 border rounded-lg font-medium"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWebhook}
                  disabled={!webhookUrl || webhookEvents.length === 0}
                  className="flex-1 py-2 rounded-lg font-medium disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  data-testid="submit-create-webhook"
                >
                  Add Webhook
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
